import { useEffect, useState } from 'react';
import { usePaneManagement } from '../../contexts/PaneManagementContext';
import { useApplicationStore, useUnifyProfileStore, useUserIPRestrictionStore, useWorkflowsStore } from '../../Store';
import { AMPLITUDE_EVENTS, DRIVE_TITLE_ID } from '../../constants';
import { IUserIPRestrictionEntity } from '../DealerAdmin/IPRestrictionInterface';
import { useAnalytics } from '../../v2/third-party-integration/amplitude';
import { setDocumentTitle } from '../../util/TitleManager';
import { useStartupDriveStore } from '../../Store/useStartupDriveStore';
import { useFeatureFlag } from '@harnessio/ff-react-client-sdk';

const STARTUP_REDIRECT_KEY = 'unify-startup-redirect-done';

export default function StartupAppRedirect() {
  const [done, setDone] = useState(false);
  const { openPane, toggleIsPaneExpanded } = usePaneManagement();
  const applications = useApplicationStore((state: any) => state.applications);
  const isFetched = useApplicationStore((state: any) => state.isFetched);
  const { unifyProfile } = useUnifyProfileStore();
  const ipRestriction = useUserIPRestrictionStore((state: any) => state.ipRestriction);
  const workflows = useWorkflowsStore((state: any) => state.workflows);
  const isWorkflowsFetched = useWorkflowsStore((state: any) => state.isFetched);
  const { trackEvent } = useAnalytics();
  const setShouldLaunchDrive = useStartupDriveStore((state) => state.setShouldLaunchDrive);
  const startupAppFlag = useFeatureFlag('UNIFY_STARTUP_APP');

  useEffect(() => {
    if (done) return;
    if (startupAppFlag === undefined) return; // SDK still loading — wait
    if (startupAppFlag === false) {
      setDone(true); // explicitly off — lock in for session
      localStorage.setItem(STARTUP_REDIRECT_KEY, 'true');
      return;
    }
    if (!isFetched || !applications?.length) return;
    if (!unifyProfile?.apps || !Array.isArray(unifyProfile.apps)) return;
    if (!unifyProfile?.workflows || !Array.isArray(unifyProfile.workflows)) return;
    if (!Array.isArray(ipRestriction)) return;
    if (!isWorkflowsFetched) return;
    // Already redirected this login session (flag cleared on logout via clearLocalStorageExceptDMS)
    if (localStorage.getItem(STARTUP_REDIRECT_KEY) === 'true') {
      setDone(true);
      return;
    }

    // Skip if deep link is pending
    const deepLink = localStorage.getItem('deep-link-url');
    if (deepLink) {
      setDone(true);
      localStorage.setItem(STARTUP_REDIRECT_KEY, 'true');
      return;
    }

    // Skip if on admin/special routes
    const adminPath = /^\/(admin|pulse|dealer-admin|global-search)/;
    if (adminPath.test(window.location.pathname)) {
      setDone(true);
      localStorage.setItem(STARTUP_REDIRECT_KEY, 'true');
      return;
    }

    // Check for default app first
    const defaultApp = unifyProfile.apps.find(
      (app: any) => app.isDefaultApp === true
    );

    if (!defaultApp) {
      // No default app — check for default workflow
      const defaultWorkflow = unifyProfile.workflows.find(
        (wf: any) => wf.isDefaultWorkflow === true
      );

      if (!defaultWorkflow) {
        setDone(true);
        localStorage.setItem(STARTUP_REDIRECT_KEY, 'true');
        return;
      }

      const workflowId = defaultWorkflow.workflowId;
      const appId = defaultWorkflow.appId;

      const appRestrictionEntry = ipRestriction.find(
        (item: IUserIPRestrictionEntity) =>
          item.entityType === 'application' &&
          item.entityId === appId
      );

      let isRestricted: boolean;
      if (appRestrictionEntry?.isRestricted) {
        isRestricted = true;
      } else if (appRestrictionEntry?.isIpConfigured) {
        isRestricted = false;
      } else {
        isRestricted = ipRestriction.some(
          (item: IUserIPRestrictionEntity) =>
            item.entityType === 'workflow' &&
            item.entityId === workflowId &&
            item.isRestricted
        );
      }

      if (isRestricted) {
        setDone(true);
        localStorage.setItem(STARTUP_REDIRECT_KEY, 'true');
        return;
      }

      const workflowDetails = workflows?.find((wf: any) => wf.id === workflowId);

      if (!workflowDetails?.url || !workflowDetails.url.startsWith('http')) {
        setDone(true);
        localStorage.setItem(STARTUP_REDIRECT_KEY, 'true');
        return;
      }

      setDone(true);
      localStorage.setItem(STARTUP_REDIRECT_KEY, 'true');
      setTimeout(() => {
        openPane(workflowId);
        toggleIsPaneExpanded(true);
        setDocumentTitle(workflowDetails.name, workflowDetails.url);
        trackEvent(AMPLITUDE_EVENTS.OPEN_WORKFLOW, { Workflow: workflowDetails.name });
        setTimeout(() => {
          const frameWindow: any = document.getElementById(workflowId);
          if (frameWindow) {
            frameWindow.src = workflowDetails.url;
          }
        }, 8000);
      }, 500);
      return;
    }

    // Default app found — proceed with app redirect
    const appId = defaultApp.appId;

    const isRestricted = ipRestriction.some(
      (item: IUserIPRestrictionEntity) =>
        item.entityType === 'application' &&
        item.entityId === appId &&
        item.isRestricted
    );

    if (isRestricted) {
      setDone(true);
      localStorage.setItem(STARTUP_REDIRECT_KEY, 'true');
      return;
    }

    const appDetails = applications.find((app: any) => app.id === appId);

    if (!appDetails) {
      setDone(true);
      localStorage.setItem(STARTUP_REDIRECT_KEY, 'true');
      return;
    }

    if (appId === DRIVE_TITLE_ID) {
      setDone(true);
      localStorage.setItem(STARTUP_REDIRECT_KEY, 'true');
      console.log('StartupAppRedirect: setShouldLaunchDrive true');
      setShouldLaunchDrive(true);
      return;
    }

    const appUrl = appDetails.url || '';
    if (!appUrl || !appUrl.startsWith('http')) {
      setDone(true);
      localStorage.setItem(STARTUP_REDIRECT_KEY, 'true');
      return;
    }

    const urlParams = new URLSearchParams(appUrl.split('?')[1]);
    if (urlParams.get('launchtype') === 'tab') {
      setDone(true);
      localStorage.setItem(STARTUP_REDIRECT_KEY, 'true');
      return;
    }

    setDone(true);
    localStorage.setItem(STARTUP_REDIRECT_KEY, 'true');
    setTimeout(() => {
      openPane(appId);
      toggleIsPaneExpanded(true);
      setDocumentTitle(appDetails.name, appUrl);
      trackEvent(AMPLITUDE_EVENTS.OPEN_APPLICATION, { Application: appDetails.name });
    }, 500);
  }, [done, isFetched, isWorkflowsFetched, applications, unifyProfile, ipRestriction, workflows, openPane, toggleIsPaneExpanded, trackEvent, setShouldLaunchDrive, startupAppFlag]);

  return null;
}
